import { motion } from "framer-motion";
import { BellRing, Store, QrCode, ShieldCheck, Video, Sparkles } from "lucide-react";
import { Kicker, MaskLines, Rise, SlideShell } from "@/deck/ui";

const nodes = [
  { icon: BellRing, title: "Reminder & recharge", sub: "Electricity, before it runs out" },
  { icon: Store, title: "Digital cashier", sub: "Kasir for BM & UMKM" },
  { icon: QrCode, title: "QRIS payment", sub: "Scan, pay, token sent" },
  { icon: ShieldCheck, title: "E2E-encrypted chat", sub: "With Building Management & neighbours" },
  { icon: Video, title: "AI chat & video call", sub: "Instant technical help" },
  { icon: Sparkles, title: "Daily companion", sub: "Learns your routine" },
];

const Node = ({ n, i, style }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.8 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ delay: 0.5 + i * 0.09, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
    whileHover={{ scale: 1.05 }}
    className="flex items-center gap-3 rounded-2xl border border-white/10 bg-[#0e1117]/85 px-4 py-3 backdrop-blur-xl lg:absolute lg:w-[230px] lg:-translate-x-1/2 lg:-translate-y-1/2"
    style={style}
    data-testid={`solution-node-${i + 1}`}
  >
    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-dash/15 text-[var(--dash)]">
      <n.icon size={17} />
    </span>
    <div>
      <p className="text-sm font-semibold text-zinc-50">{n.title}</p>
      <p className="text-xs text-zinc-400">{n.sub}</p>
    </div>
  </motion.div>
);

const pos = (i) => {
  const a = (-90 + i * 60) * (Math.PI / 180);
  return { left: `${50 + Math.cos(a) * 41}%`, top: `${50 + Math.sin(a) * 41}%` };
};

export default function Solution() {
  return (
    <SlideShell testId="solution-slide">
      <div className="grid flex-1 items-center gap-8 lg:min-h-0 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <Rise><Kicker index="03">The solution</Kicker></Rise>
          <MaskLines lines={["Not just an app.", "A companion."]} accentIndex={1} className="mt-4 text-4xl font-extrabold leading-[0.95] tracking-[-0.035em] sm:text-5xl lg:text-6xl" />
          <Rise delay={0.45} className="mt-6 max-w-sm">
            <p className="text-base leading-relaxed text-zinc-300">
              Dash AI is built not only to make things easier — but to be a personal assistant that accompanies every
              tenant through their daily activities.
            </p>
          </Rise>
        </div>
        <div className="relative lg:col-span-8 lg:h-full">
          <div className="relative mx-auto hidden aspect-square h-full max-h-[600px] lg:block" data-testid="solution-orbit">
            <div className="spin-slow absolute inset-[9%] rounded-full border border-dashed border-white/15" />
            <div className="spin-slower absolute inset-[24%] rounded-full border border-dash/25" />
            <div className="absolute left-1/2 top-1/2 h-[36%] w-[36%] -translate-x-1/2 -translate-y-1/2">
              <div className="pulse-ring absolute inset-0 rounded-full border border-dash/60" />
              <img src="/assets/orb.jpg" alt="Dash AI core" className="h-full w-full rounded-full object-cover [mask-image:radial-gradient(circle,black_55%,transparent_72%)]" />
            </div>
            {nodes.map((n, i) => <Node key={n.title} n={n} i={i} style={pos(i)} />)}
          </div>
          <div className="lg:hidden">
            <div className="relative mx-auto mb-6 h-44 w-44">
              <div className="pulse-ring absolute inset-0 rounded-full border border-dash/60" />
              <img src="/assets/orb.jpg" alt="Dash AI core" className="h-full w-full rounded-full object-cover [mask-image:radial-gradient(circle,black_55%,transparent_72%)]" />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {nodes.map((n, i) => <Node key={n.title} n={n} i={i} />)}
            </div>
          </div>
        </div>
      </div>
    </SlideShell>
  );
}
